import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import { getNumbers } from "@/redux/slices/numbers/thunks/getNumbers.ts";
import { CardTable } from "@template/components";
import { TNumber } from "@/redux/slices/numbers/types.ts";
import { deleteCartItem } from "@/redux/slices/user/thunks/deleteCartItem.ts";
import { postCartItem } from "@/redux/slices/user/thunks/postCartItem.ts";
import useCurrency from "@/lib/helpers/hooks/useCurrency.ts";

export interface INumberTableProps {
  type: "standard" | "premium" | "nonGeo";
  label: string;
  showAreaCode?: boolean;
}

const NumberTable = ({ type, label, showAreaCode }: INumberTableProps) => {
  const dispatch = useTypedDispatch();
  const currency = useCurrency();

  const cart = useTypedSelector((state) => state.user.cart);
  const numbers = useTypedSelector((state) => state.numbers.numbers[type]);
  const options = useTypedSelector((state) => state.numbers.options);
  const countries = useTypedSelector((state) => state.numbers.countries);
  const user = useTypedSelector((state) => state.user.user);
  const inclusive = useTypedSelector((state) => state.numbers.inclusive);

  const voip = user.voip;

  const isVoipAdded =
    (voip?.has_voip_cart_users && voip?.has_voip_cart) ||
    (voip?.has_voip_customer && !voip?.has_voip_trial) ||
    user?.has_voip;

  const countryCode = options.countryCode;
  const areaCode = options.areaCode;
  const numberCategory = options.numberCategory;
  const startsWith = options.startsWith;
  const contains = options.contains;
  const endsWith = options.endsWith;
  const showAdvanced = options.showAdvanced;

  const foundCountry = countries.find((c) => c.value === countryCode);
  const foundAreaCode = foundCountry?.area_codes?.find(
    (a) => a.area_code === areaCode,
  );
  const areaCodeDisplay =
    showAreaCode && foundAreaCode ? foundAreaCode.name : "";

  const selectedNumbers = cart.items.filter((i) => i.type.name === "Number");
  const selectedNumberKeys = selectedNumbers.map((i) =>
    i.name.replace(/-|\s/g, "")
  );

  const onChangePage = (page: number) =>
    dispatch(
      getNumbers({
        countryCode: countries.find((c) => c.value === countryCode)
          ?.countryCode as any,
        type: type,
        areaCode: foundAreaCode ? foundAreaCode.search_key : areaCode,
        pattern: `${foundAreaCode?.area_code_block || ""}${startsWith}*${contains}*${endsWith}`,
        page: page + 1,
      })
    );

  const isPhoneNumberAdded = (phoneNumber: string) => {
    const filteredCartItems = cart?.items.filter((item) =>
      item.name.replace(/-|\s/g, "").includes(phoneNumber)
    );

    return filteredCartItems?.length > 0;
  };

  const getItemId = (phoneNumber: string) =>
    cart?.items
      .filter((item) => item.name.replace(/-|\s/g, "").includes(phoneNumber))
      .map((item) => item.id)[0];

  const getCartInclusiveItems = () =>
    cart?.items.filter((item) => item.type.sub_type_id === 0) || [];

  const getInclusiveItemCartIndex = (phoneNumber: string) => {
    let itemIndex: number;

    getCartInclusiveItems().forEach((item, index) => {
      if (item.id === getItemId(phoneNumber)) {
        itemIndex = index;
      }
    });

    // @ts-ignore
    return itemIndex;
  };

  const getPriceDisplay = (number: TNumber) => {
    const canHaveInclusive =
      ["standard", "non-geo"].includes(type) &&
      number.price_category === 0 &&
      (inclusive.cart_remaining > 0 ||
        (inclusive.cart_remaining <= 0 &&
          isPhoneNumberAdded(number.number) &&
          getInclusiveItemCartIndex(number.number) <
          inclusive.inclusive_remaining));
    return canHaveInclusive
      ? "Inclusive"
      : `${currency}${(number.price / 100).toFixed(2)}/month`;
  };

  const onAddNumber = (number: TNumber) => {
    dispatch(
      postCartItem({
        itemData: {
          area_code: number.area_code,
          country_code: number.country_code,
          number: number.number,
        },
        itemType: 4,
        itemSubType: number.price_category,
      })
    );
  };

  const onDeleteNumber = (number: TNumber) => {
    const cartItem = selectedNumbers.find(
      (i) =>
        i.name.replace(/-|\s/g, "") ===
        number.number_pretty.replace(/-|\s/g, "")
    );
    if (!cartItem) return;
    dispatch(deleteCartItem(cartItem.id));
  };

  const onSelect = (key: string) => {
    const number = numbers.numbers.find((n) =>
      n.number_pretty.replace(/-|\s/g, "").includes(key)
    );
    const isInCart = selectedNumberKeys.some((x) => x.includes(key));
    if (!number) return;
    if (!isInCart) {
      onAddNumber(number);
    } else {
      onDeleteNumber(number);
    }
  };

  const hasNoResults =
    (numberCategory !== "all" && numberCategory !== type) ||
    (!numbers.loading && numbers.numbers.length === 0);

  const hasAdvancedSearch =
    showAdvanced && (startsWith || contains || endsWith);

  const tableLabel = () => {
    let usedLabel = <></>;
    if (hasNoResults) {
      if (hasAdvancedSearch) {
        usedLabel = (
          <>
            No {label} {areaCodeDisplay} numbers found matching your search
            <br />
            <br />
            Try again with different options
          </>
        );
      } else {
        usedLabel = (
          <>
            Due to availability issues, we no longer provide {label}{" "}
            {areaCodeDisplay} numbers. Please see our available Memorable
            numbers below for this area code.
          </>
        );
      }
    } else {
      usedLabel = (
        <>
          Buy {label} {areaCodeDisplay} Phone Numbers
        </>
      );
    }

    return usedLabel;
  };

  return (
    <CardTable
      button={
        isVoipAdded
          ? {
            children: "Check Out",
            navigation: {
              href: "/shopping-cart",
            },
          }
          : {
            children: "Add Plan",
            navigation: {
              href: "/voice",
            },
          }
      }
      items={numbers.numbers.map((n) => ({
        key: n.number,
        label: n.number_pretty,
        subheading: getPriceDisplay(n),
        selected: selectedNumberKeys.some((k) =>
          n.number_pretty.replace(/-|\s/g, "").includes(k)
        ),
        loading:
          selectedNumbers.some(
            (i) =>
              i.name
                .replace(/-|\s/g, "")
                .includes(n.number_pretty.replace(/-|\s/g, "")) && i.loading
          ) || !!selectedNumbers.find((num) => num.id === n.number),
      }))}
      label={tableLabel()}
      loading={numbers.loading}
      onSelect={onSelect}
      page={numbers.page - 1}
      onPageChange={onChangePage}
      overrideContent={hasNoResults}
      pages={numbers.total_pages}
    />
  );
};

export default NumberTable;
